import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { VideoMetricsSort } from "@unbox/api";
import { VIDEO_NEGATABLE_FILTERS } from "@unbox/api";
import type { InferResponseType } from "hono";
import { useCallback } from "react";
import { handleError } from "@/api/error";
import type { ChunkResponse } from "@/hooks/use-progressive-fetch";
import { toNotQuery } from "@/providers/filter-registry";
import { type Pagination, toQuery } from "./pagination";
import { client, type MerchantScope, useScopedMerchantClient } from "./scoped-merchant-client";

export type TimePeriod = "7d" | "30d" | "all_time";

type VideoMetricsFilters = {
  text: string;
  publishDateStart?: string;
  publishDateEnd?: string;
  riskId?: string[];
  safetyStatus?: string[];
  riskTier?: string[];
  actionTaken?: string[];
  creatorType?: "affiliate" | "brand";
  maxGmv?: number;
  minGmv?: number;
  productIds?: string[];
  hooks?: string[];
  tactics?: string[];
  videoStyles?: string[];
  sellingPoints?: string[];
  creatorIds?: string[];
  creatorListIds?: string[];
  creatorClassifications?: string[];
  minViews?: number;
  maxViews?: number;
  minCtr?: number;
  maxCtr?: number;
  minCtor?: number;
  maxCtor?: number;
  minOrders?: number;
  maxOrders?: number;
  videoRiskStatus?: "no_risk" | "has_risk";
  creatorLevel?: string[];
  minFollowers?: number;
  maxFollowers?: number;
  maleAudiencePercent?: ">25" | ">50" | ">75";
  femaleAudiencePercent?: ">25" | ">50" | ">75";
  age?: ("18-24" | "25-34" | "35-44" | "45-54" | "55+")[];
  collaborationType?: "open" | "target";
  collaborationIds?: string[];
  campaignIds?: string[];
  videoLanguages?: string[];
  not?: string[];
};
export type VideoMetricsParams = { timePeriod: TimePeriod } & VideoMetricsFilters;
export type VideoMetricsSortParam = { column: VideoMetricsSort; direction: "asc" | "desc" };

const videoMetricsKey = (
  scope: MerchantScope,
  {
    timePeriod,
    text,
    publishDateStart,
    publishDateEnd,
    riskTier,
    riskId,
    safetyStatus,
    actionTaken,
    creatorType,
    maxGmv,
    minGmv,
    productIds,
    hooks,
    tactics,
    videoStyles,
    sellingPoints,
    creatorIds,
    creatorListIds,
    creatorClassifications,
    minViews,
    maxViews,
    minCtr,
    maxCtr,
    minCtor,
    maxCtor,
    minOrders,
    maxOrders,
    videoRiskStatus,
    creatorLevel,
    minFollowers,
    maxFollowers,
    maleAudiencePercent,
    femaleAudiencePercent,
    age,
    collaborationType,
    collaborationIds,
    campaignIds,
    videoLanguages,
    not,
    ...rest
  }: VideoMetricsParams,
  pagination: Pagination,
  sort: VideoMetricsSortParam,
) => {
  rest satisfies Record<string, never>;
  return [
    "video-overview",
    scope.merchantId,
    scope.workspaceId,
    timePeriod,
    pagination.limit,
    pagination.offset,
    text,
    sort.column,
    sort.direction,
    publishDateStart,
    publishDateEnd,
    riskId?.toString(),
    safetyStatus?.toString(),
    riskTier?.toString(),
    actionTaken?.toString(),
    creatorType,
    maxGmv,
    minGmv,
    productIds?.toString(),
    hooks?.toString(),
    videoStyles?.toString(),
    sellingPoints?.toString(),
    tactics?.toString(),
    creatorIds?.toString(),
    creatorListIds?.toString(),
    creatorClassifications?.toString(),
    minViews,
    maxViews,
    minCtr,
    maxCtr,
    minCtor,
    maxCtor,
    minOrders,
    maxOrders,
    videoRiskStatus,
    creatorLevel?.toString(),
    minFollowers,
    maxFollowers,
    maleAudiencePercent,
    femaleAudiencePercent,
    age?.toString(),
    collaborationType,
    collaborationIds?.toString(),
    campaignIds?.toString(),
    videoLanguages?.toString(),
    not?.toString(),
  ];
};

const fetchVideoMetrics = async (
  scope: MerchantScope,
  {
    timePeriod,
    text,
    publishDateStart,
    publishDateEnd,
    riskTier,
    riskId,
    safetyStatus,
    actionTaken,
    creatorType,
    maxGmv,
    minGmv,
    productIds,
    hooks,
    tactics,
    videoStyles,
    sellingPoints,
    creatorIds,
    creatorListIds,
    creatorClassifications,
    minViews,
    maxViews,
    minCtr,
    maxCtr,
    minCtor,
    maxCtor,
    minOrders,
    maxOrders,
    videoRiskStatus,
    creatorLevel,
    minFollowers,
    maxFollowers,
    maleAudiencePercent,
    femaleAudiencePercent,
    age,
    collaborationType,
    collaborationIds,
    campaignIds,
    videoLanguages,
    not,
  }: VideoMetricsParams,
  pagination: Pagination,
  sort: VideoMetricsSortParam,
) => {
  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].explorer.videos.$get({
        param: scope.param,
        query: {
          timePeriod,
          ...toQuery(pagination),
          text: text === "" ? undefined : text,
          column: sort.column,
          direction: sort.direction,
          publishDateStart,
          publishDateEnd,
          riskTier,
          actionTaken,
          riskId,
          safetyStatus,
          creatorType,
          maxGmv: maxGmv?.toString(),
          minGmv: minGmv?.toString(),
          productIds,
          hooks,
          videoStyles,
          sellingPoints,
          tactics,
          creatorIds,
          creatorListIds,
          creatorClassifications,
          minViews: minViews?.toString(),
          maxViews: maxViews?.toString(),
          minCtr: minCtr?.toString(),
          maxCtr: maxCtr?.toString(),
          minCtor: minCtor?.toString(),
          maxCtor: maxCtor?.toString(),
          minOrders: minOrders?.toString(),
          maxOrders: maxOrders?.toString(),
          videoRiskStatus,
          creatorLevel,
          minFollowers: minFollowers?.toString(),
          maxFollowers: maxFollowers?.toString(),
          maleAudiencePercent,
          femaleAudiencePercent,
          age,
          collaborationType,
          collaborationIds,
          campaignIds,
          videoLanguages,
          not: toNotQuery(not, VIDEO_NEGATABLE_FILTERS),
        },
      })
    : await client.merchant[":merchantId"].explorer.videos.$get({
        param: scope.param,
        query: {
          timePeriod,
          ...toQuery(pagination),
          text: text === "" ? undefined : text,
          column: sort.column,
          direction: sort.direction,
          publishDateStart,
          publishDateEnd,
          riskTier,
          actionTaken,
          riskId,
          safetyStatus,
          creatorType,
          maxGmv: maxGmv?.toString(),
          minGmv: minGmv?.toString(),
          productIds,
          hooks,
          videoStyles,
          sellingPoints,
          tactics,
          creatorIds,
          creatorListIds,
          creatorClassifications,
          minViews: minViews?.toString(),
          maxViews: maxViews?.toString(),
          minCtr: minCtr?.toString(),
          maxCtr: maxCtr?.toString(),
          minCtor: minCtor?.toString(),
          maxCtor: maxCtor?.toString(),
          minOrders: minOrders?.toString(),
          maxOrders: maxOrders?.toString(),
          videoRiskStatus,
          creatorLevel,
          minFollowers: minFollowers?.toString(),
          maxFollowers: maxFollowers?.toString(),
          maleAudiencePercent,
          femaleAudiencePercent,
          age,
          collaborationType,
          collaborationIds,
          campaignIds,
          videoLanguages,
          not: toNotQuery(not, VIDEO_NEGATABLE_FILTERS),
        },
      });
  if (!res.ok) throw await handleError(res);
  return await res.json();
};

const videosCountKey = (
  scope: MerchantScope,
  {
    timePeriod,
    text,
    publishDateStart,
    publishDateEnd,
    riskTier,
    riskId,
    safetyStatus,
    actionTaken,
    creatorType,
    maxGmv,
    minGmv,
    productIds,
    hooks,
    videoStyles,
    sellingPoints,
    tactics,
    creatorIds,
    creatorListIds,
    creatorClassifications,
    minViews,
    maxViews,
    minCtr,
    maxCtr,
    minCtor,
    maxCtor,
    minOrders,
    maxOrders,
    videoRiskStatus,
    creatorLevel,
    minFollowers,
    maxFollowers,
    maleAudiencePercent,
    femaleAudiencePercent,
    age,
    collaborationType,
    collaborationIds,
    campaignIds,
    videoLanguages,
    not,
    ...rest
  }: VideoMetricsParams,
) => {
  rest satisfies Record<string, never>;
  return [
    "videos-count",
    scope.merchantId,
    scope.workspaceId,
    timePeriod,
    text,
    publishDateStart,
    publishDateEnd,
    riskId?.toString(),
    safetyStatus?.toString(),
    riskTier?.toString(),
    actionTaken?.toString(),
    creatorType,
    maxGmv,
    minGmv,
    productIds?.toString(),
    hooks?.toString(),
    videoStyles?.toString(),
    sellingPoints?.toString(),
    tactics?.toString(),
    creatorIds?.toString(),
    creatorListIds?.toString(),
    creatorClassifications?.toString(),
    minViews,
    maxViews,
    minCtr,
    maxCtr,
    minCtor,
    maxCtor,
    minOrders,
    maxOrders,
    videoRiskStatus,
    creatorLevel?.toString(),
    minFollowers,
    maxFollowers,
    maleAudiencePercent,
    femaleAudiencePercent,
    age?.toString(),
    collaborationType,
    collaborationIds?.toString(),
    campaignIds?.toString(),
    videoLanguages?.toString(),
    not?.toString(),
  ];
};

const fetchVideosCount = async (
  scope: MerchantScope,
  {
    timePeriod,
    text,
    publishDateStart,
    publishDateEnd,
    riskTier,
    riskId,
    safetyStatus,
    actionTaken,
    creatorType,
    maxGmv,
    minGmv,
    productIds,
    hooks,
    videoStyles,
    sellingPoints,
    tactics,
    creatorIds,
    creatorListIds,
    creatorClassifications,
    minViews,
    maxViews,
    minCtr,
    maxCtr,
    minCtor,
    maxCtor,
    minOrders,
    maxOrders,
    videoRiskStatus,
    creatorLevel,
    minFollowers,
    maxFollowers,
    maleAudiencePercent,
    femaleAudiencePercent,
    age,
    collaborationType,
    collaborationIds,
    campaignIds,
    videoLanguages,
    not,
  }: VideoMetricsParams,
) => {
  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].explorer.videos.count.$get({
        param: scope.param,
        query: {
          timePeriod,
          text: text === "" ? undefined : text,
          publishDateStart,
          publishDateEnd,
          riskTier,
          actionTaken,
          riskId,
          safetyStatus,
          creatorType,
          maxGmv: maxGmv?.toString(),
          minGmv: minGmv?.toString(),
          productIds,
          hooks,
          videoStyles,
          sellingPoints,
          tactics,
          creatorIds,
          creatorListIds,
          creatorClassifications,
          minViews: minViews?.toString(),
          maxViews: maxViews?.toString(),
          minCtr: minCtr?.toString(),
          maxCtr: maxCtr?.toString(),
          minCtor: minCtor?.toString(),
          maxCtor: maxCtor?.toString(),
          minOrders: minOrders?.toString(),
          maxOrders: maxOrders?.toString(),
          videoRiskStatus,
          creatorLevel,
          minFollowers: minFollowers?.toString(),
          maxFollowers: maxFollowers?.toString(),
          maleAudiencePercent,
          femaleAudiencePercent,
          age,
          collaborationType,
          collaborationIds,
          campaignIds,
          videoLanguages,
          not: toNotQuery(not, VIDEO_NEGATABLE_FILTERS),
        },
      })
    : await client.merchant[":merchantId"].explorer.videos.count.$get({
        param: scope.param,
        query: {
          timePeriod,
          text: text === "" ? undefined : text,
          publishDateStart,
          publishDateEnd,
          riskTier,
          actionTaken,
          riskId,
          safetyStatus,
          creatorType,
          maxGmv: maxGmv?.toString(),
          minGmv: minGmv?.toString(),
          productIds,
          hooks,
          videoStyles,
          sellingPoints,
          tactics,
          creatorIds,
          creatorListIds,
          creatorClassifications,
          minViews: minViews?.toString(),
          maxViews: maxViews?.toString(),
          minCtr: minCtr?.toString(),
          maxCtr: maxCtr?.toString(),
          minCtor: minCtor?.toString(),
          maxCtor: maxCtor?.toString(),
          minOrders: minOrders?.toString(),
          maxOrders: maxOrders?.toString(),
          videoRiskStatus,
          creatorLevel,
          minFollowers: minFollowers?.toString(),
          maxFollowers: maxFollowers?.toString(),
          maleAudiencePercent,
          femaleAudiencePercent,
          age,
          collaborationType,
          collaborationIds,
          campaignIds,
          videoLanguages,
          not: toNotQuery(not, VIDEO_NEGATABLE_FILTERS),
        },
      });
  if (!res.ok) throw await handleError(res);
  return await res.json();
};

export const useVideoMetrics = ({
  pagination,
  sort,
  ...params
}: VideoMetricsParams & { pagination: Pagination; sort: VideoMetricsSortParam }) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: videoMetricsKey(scope, params, pagination, sort),
    queryFn: () => fetchVideoMetrics(scope, params, pagination, sort),
    enabled: !!scope.merchantId,
  });
};

export const useVideosCount = (params: VideoMetricsParams) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: videosCountKey(scope, params),
    queryFn: () => fetchVideosCount(scope, params),
    enabled: !!scope.merchantId,
  });
};

export const useVideoMetricRowsFetcher = ({
  sort,
  ...params
}: VideoMetricsParams & { sort: VideoMetricsSortParam }) => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useCallback(
    async (offset: number, limit: number): Promise<ChunkResponse<VideoMetricsResponse["videos"][number]>> => {
      const pagination = { limit, offset };
      const [list, count] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: videoMetricsKey(scope, params, pagination, sort),
          queryFn: () => fetchVideoMetrics(scope, params, pagination, sort),
          staleTime: Number.POSITIVE_INFINITY,
        }),
        queryClient.fetchQuery({
          queryKey: videosCountKey(scope, params),
          queryFn: () => fetchVideosCount(scope, params),
          staleTime: Number.POSITIVE_INFINITY,
        }),
      ]);
      return { items: list.videos, total: count.totalVideos };
    },
    [queryClient, scope, params, sort],
  );
};

const CHUNK_SIZE = 200;
const MAX_EXPORT_ROWS = 10000;
const MAX_CONCURRENT_REQUESTS = 5;

export class ExportLimitExceededError extends Error {
  constructor(
    public total: number,
    public limit: number,
  ) {
    super(`Export limit exceeded: ${total} videos exceeds the maximum of ${limit}`);
    this.name = "ExportLimitExceededError";
  }
}

async function fetchInBatches<T>(requests: (() => Promise<T>)[], concurrency: number): Promise<T[]> {
  const results: T[] = [];

  for (let i = 0; i < requests.length; i += concurrency) {
    const batch = requests.slice(i, i + concurrency);
    const batchResults = await Promise.all(batch.map((fn) => fn()));
    results.push(...batchResults);
  }

  return results;
}

export const useVideoMetricsDownload = () => {
  const scope = useScopedMerchantClient();

  return useMutation<
    VideoMetricsResponse["videos"],
    Error,
    {
      total: number;
      timePeriod: TimePeriod;
      sort: { column: VideoMetricsSort; direction: "asc" | "desc" };
    } & VideoMetricsFilters
  >({
    mutationFn: async ({
      total,
      sort,
      text,
      timePeriod,
      publishDateStart,
      publishDateEnd,
      riskTier,
      actionTaken,
      riskId,
      safetyStatus,
      creatorType,
      maxGmv,
      minGmv,
      productIds,
      hooks,
      videoStyles,
      sellingPoints,
      tactics,
      creatorIds,
      creatorListIds,
      creatorClassifications,
      minViews,
      maxViews,
      minCtr,
      maxCtr,
      minCtor,
      maxCtor,
      minOrders,
      maxOrders,
      videoRiskStatus,
      creatorLevel,
      minFollowers,
      maxFollowers,
      maleAudiencePercent,
      femaleAudiencePercent,
      age,
      collaborationType,
      collaborationIds,
      campaignIds,
      videoLanguages,
      not,
      ...rest
    }) => {
      rest satisfies Record<string, never>;

      if (!scope.merchantId) {
        throw new Error("Merchant is required for export");
      }

      if (total > MAX_EXPORT_ROWS) {
        throw new ExportLimitExceededError(total, MAX_EXPORT_ROWS);
      }

      const chunks = [...Array(Math.ceil(total / CHUNK_SIZE)).keys()];

      const requests = chunks.map((page) => async () => {
        const res = scope.workspaceId
          ? await client.workspace[":workspaceId"].merchant[":merchantId"].explorer.videos.$get({
              param: scope.param,
              query: {
                timePeriod,
                limit: CHUNK_SIZE.toString(),
                offset: (CHUNK_SIZE * page).toString(),
                text: text === "" ? undefined : text,
                column: sort.column,
                direction: sort.direction,
                publishDateStart,
                publishDateEnd,
                riskTier,
                actionTaken,
                riskId,
                safetyStatus,
                creatorType,
                maxGmv: maxGmv?.toString(),
                minGmv: minGmv?.toString(),
                productIds,
                hooks,
                videoStyles,
                sellingPoints,
                tactics,
                creatorIds,
                creatorListIds,
                creatorClassifications,
                minViews: minViews?.toString(),
                maxViews: maxViews?.toString(),
                minCtr: minCtr?.toString(),
                maxCtr: maxCtr?.toString(),
                minCtor: minCtor?.toString(),
                maxCtor: maxCtor?.toString(),
                minOrders: minOrders?.toString(),
                maxOrders: maxOrders?.toString(),
                videoRiskStatus,
                creatorLevel,
                minFollowers: minFollowers?.toString(),
                maxFollowers: maxFollowers?.toString(),
                maleAudiencePercent,
                femaleAudiencePercent,
                age,
                collaborationType,
                collaborationIds,
                campaignIds,
                videoLanguages,
                not: toNotQuery(not, VIDEO_NEGATABLE_FILTERS),
              },
            })
          : await client.merchant[":merchantId"].explorer.videos.$get({
              param: scope.param,
              query: {
                timePeriod,
                limit: CHUNK_SIZE.toString(),
                offset: (CHUNK_SIZE * page).toString(),
                text: text === "" ? undefined : text,
                column: sort.column,
                direction: sort.direction,
                publishDateStart,
                publishDateEnd,
                riskTier,
                actionTaken,
                riskId,
                safetyStatus,
                creatorType,
                maxGmv: maxGmv?.toString(),
                minGmv: minGmv?.toString(),
                productIds,
                hooks,
                videoStyles,
                sellingPoints,
                tactics,
                creatorIds,
                creatorListIds,
                creatorClassifications,
                minViews: minViews?.toString(),
                maxViews: maxViews?.toString(),
                minCtr: minCtr?.toString(),
                maxCtr: maxCtr?.toString(),
                minCtor: minCtor?.toString(),
                maxCtor: maxCtor?.toString(),
                minOrders: minOrders?.toString(),
                maxOrders: maxOrders?.toString(),
                videoRiskStatus,
                creatorLevel,
                minFollowers: minFollowers?.toString(),
                maxFollowers: maxFollowers?.toString(),
                maleAudiencePercent,
                femaleAudiencePercent,
                age,
                collaborationType,
                collaborationIds,
                campaignIds,
                videoLanguages,
                not: toNotQuery(not, VIDEO_NEGATABLE_FILTERS),
              },
            });
        if (!res.ok) throw await handleError(res);
        return res.json();
      });

      try {
        const response = await fetchInBatches(requests, MAX_CONCURRENT_REQUESTS);
        return response.flatMap((res) => res.videos);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
  });
};

const req = client.merchant[":merchantId"].explorer.videos.$get;
export type VideoMetricsResponse = InferResponseType<typeof req>;
